import { FormattedMessage, useIntl } from "react-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./RedeemDialog.module.css";
import { clsx } from "clsx";
import {
  DialogBody,
  DialogFooter,
  DialogFrame,
} from "../../../../common/core-ui/DialogFrame.tsx";
import { useRedeemFetcher } from "./redeem.ts";
import TextInput from "../../../../common/core-ui/TextInput.tsx";
import useToasts from "../../../root/core-ui/toast/useToasts.ts";
import ErrorBoundary from "../../../../common/core-ui/ErrorBoundary.tsx";
import LoaderButton from "../../../../common/core-ui/LoaderButton.tsx";
import { deferFn } from "../../../../common/utils/function.ts";
import RedeemToast from "./RedeemToast.tsx";
import { useIsFullScreenDialog } from "../../../../common/core-ui/dialogFrameHelpers.ts";

const MAX_CODES = 4;

enum ValidationError {
  None,
  Blank,
  WrongCodeLength,
}

interface RedeemDialogProps {
  onRequestClose: () => void;
}

type RedeemDialogInnerProps = RedeemDialogProps & {
  fetcher: ReturnType<typeof useRedeemFetcher>;
  fullScreen: boolean;
};

function isValidCode(code: string) {
  // normally codes are 20 chars but explore codes are 12
  return code.length === 20 || code.length === 12;
}

function getFieldName(i: number) {
  return `code-${i}`;
}

function RedeemDialogInner({
  fetcher,
  onRequestClose,
  fullScreen,
}: RedeemDialogInnerProps) {
  const intl = useIntl();
  const [codeCount, setCodeCount] = useState(1);
  const containerRef = useRef<HTMLFormElement>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<number, ValidationError>
  >({});
  // use root toast container as toasts in dialog will disappear on close
  const { showToast } = useToasts();
  const submitting = fetcher.state !== "idle";

  const getInputs = useCallback(() => {
    return Array.from(containerRef.current?.querySelectorAll("input") ?? []);
  }, []);

  const getFirstInvalidInput = useCallback(() => {
    return containerRef.current?.querySelector<HTMLInputElement>(
      'input[aria-invalid="true"]'
    );
  }, []);

  const focusFirstInvalidInput = useCallback(() => {
    getFirstInvalidInput()?.focus();
  }, [getFirstInvalidInput]);

  /**
   * Focus first input on open
   */
  useEffect(() => {
    // a11ydialog sets focus to frame on show, we can't change that and a11ydialog
    // autofocus attr is removed by React, so we need to do it manually after mount
    deferFn(() => containerRef.current?.querySelector("input")?.focus(), 10);
  }, []);

  /**
   * Focus newly-created inputs
   */
  useEffect(() => {
    containerRef.current?.querySelectorAll("input")[codeCount - 1]?.focus();
  }, [codeCount]);

  useEffect(() => {
    if (fetcher.state !== "submitting" && fetcher.data) {
      for (const code of Object.keys(fetcher.data)) {
        const resp = fetcher.data[code];
        showToast(
          <RedeemToast
            ok={resp.isOk}
            books={resp.isOk ? resp.results.books : undefined}
            code={code}
            errorCode={resp.isOk ? undefined : resp.errorCode}
          />
        );
        // can't have toast in dialog as user might close and lose them
        // so toast go on the outside and we auto close
        onRequestClose();
      }
    }
  }, [fetcher.data, fetcher.state, onRequestClose, showToast]);

  function validate() {
    const inputs = getInputs();
    const codeCharacterCount = inputs.reduce<number>(
      (acc, el) => acc + el.value.trim().length,
      0
    );
    const errors: Record<number, ValidationError> = {};
    if (codeCharacterCount === 0) {
      // none of the fields have any code data
      errors[0] = ValidationError.Blank;
    } else {
      inputs.forEach((input, i) => {
        const val = input.value.trim();
        if (
          val.length !== 0 &&
          (!isValidCode(val) || val.trim().includes(" "))
        ) {
          errors[i] = ValidationError.WrongCodeLength;
        }
      });
    }
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return false;
    }
    return true;
  }

  function getValidationProps(i: number) {
    const error = validationErrors[i];
    if (error === ValidationError.Blank) {
      return {
        validationStatus: "invalid" as const,
        validationMessage: (
          <FormattedMessage defaultMessage="Code cannot be blank." />
        ),
      };
    } else if (error === ValidationError.WrongCodeLength) {
      return {
        validationStatus: "invalid" as const,
        validationMessage: (
          <FormattedMessage defaultMessage="Code must be either 12 or 20 characters without spaces" />
        ),
      };
    }
    return {};
  }

  return (
    <fetcher.Form
      className="vst-dialog-component-wrapper"
      method="post"
      action="/actions/redeem"
      ref={containerRef}
      onSubmit={(e) => {
        if (!validate()) {
          e.preventDefault();
          // deferred as we need aria-invalid to hit the dom
          deferFn(() => focusFirstInvalidInput());
        }
      }}
    >
      <DialogBody>
        <p className={styles.instrux}>
          <FormattedMessage
            defaultMessage="Enter the <strong>12 or 20 character</strong> code provided by your instructor, bookstore or third party store."
            values={{
              strong: (content) => <strong>{content}</strong>,
            }}
          />
        </p>
        <div className={styles.fields}>
          {Array.from({ length: codeCount }, (_, i) => {
            return (
              <TextInput
                key={i}
                disabled={submitting}
                labelText={
                  codeCount === 1 ? (
                    <FormattedMessage defaultMessage="Code" />
                  ) : (
                    <FormattedMessage
                      defaultMessage={"Code {number} of {total}"}
                      values={{ number: i + 1, total: codeCount }}
                    />
                  )
                }
                {...getValidationProps(i)}
                autoComplete="off"
                type="text"
                name={getFieldName(i)}
                onChange={() =>
                  setValidationErrors((errors) => ({
                    ...errors,
                    [i]: ValidationError.None,
                  }))
                }
              />
            );
          })}
        </div>
        {codeCount < MAX_CODES && (
          <button
            className={clsx("vst-link", styles.addMore)}
            disabled={submitting}
            onClick={() => setCodeCount((current) => current + 1)}
            type="button"
          >
            <FormattedMessage defaultMessage="Add Another" />
          </button>
        )}
      </DialogBody>
      <DialogFooter reverseButtons={fullScreen}>
        <button
          key="cancel"
          className="vst-button-rectangle vst-button-variant-naked"
          type="button"
          disabled={submitting}
          onClick={onRequestClose}
        >
          <FormattedMessage defaultMessage="Cancel" />
        </button>
        <LoaderButton
          key="redeem"
          className="vst-button-rectangle"
          loadingContext={intl.formatMessage({ defaultMessage: "Redeem Code" })}
          loading={submitting}
        >
          <FormattedMessage defaultMessage="Redeem" />
        </LoaderButton>
      </DialogFooter>
    </fetcher.Form>
  );
}

export default function RedeemDialog({ onRequestClose }: RedeemDialogProps) {
  const intl = useIntl();
  const fetcher = useRedeemFetcher();
  const fullScreen = useIsFullScreenDialog();

  return (
    <DialogFrame
      size="small"
      title={intl.formatMessage({ defaultMessage: "Redeem Code" })}
      onRequestClose={onRequestClose}
      canClose={fetcher.state === "idle"}
      fullScreen={fullScreen}
    >
      <ErrorBoundary>
        <RedeemDialogInner
          fetcher={fetcher}
          onRequestClose={onRequestClose}
          fullScreen={fullScreen}
        />
      </ErrorBoundary>
    </DialogFrame>
  );
}
